import { useCallback, useMemo } from 'react';
import Helmet from 'react-helmet';
import type { VoidFunctionComponent } from 'react';

import { ISSUES_MALWARE } from '../../../../shared/featureFlags/flagNames';
import { isFreeTier } from '../../../../shared/subscriptions/utils';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features/featureFlags';
import { useIsSecurityEnabled } from '../../../helpers/features/security';
import { useFetchPolicy } from '../../../hooks/api/policies/useFetchPolicy';
import { useSaveSecurityPolicyRules } from '../../../hooks/api/policies/useSaveSecurityPolicyRules';
import { useFetchCweList } from '../../../hooks/api/weaknesses/useFetchCweList';
import useEditable from '../../../hooks/useEditable';
import * as api from '../../../redux/api';
import FallbackImage from '../../generic/FallbackImage';
import { LabeledToggle } from '../../generic/Forms/LabeledToggle';
import PaidFeatureGate from '../../generic/Gates/PaidFeatureGate';
import Loader from '../../generic/Loader';
import NotFound from '../../generic/NotFound';
import SettingsPage from '../../shared/settings/SettingsPage';
import Header from '../../shared/settings/SettingsPageHeader';
import Section from '../../shared/settings/SettingsPageSection';
import EditPolicyCTA from '../EditPolicyCTA';
import type { SecurityRule } from '../../../../shared/model-typings';
import type { SecurityPolicy } from '../../../../typings/api';
import type { CWEDescription, RouterProps } from '../../../types';

import CVEFilterComponent from './CVEFilterComponent';
import CVSSFilterComponent from './CVSSFilterComponent';
import ItemizedRuleList from './ItemizedRuleList';
import type { Suggestion } from './ItemizedRuleList/types';
import type { CVSSRule } from './types';

type DisplaySecurityPolicyPageProps = RouterProps<{
  policyId: string;
}>;

type ContentProps = {
  policy: SecurityPolicy;
  isFreePlan: boolean;
};

const DEFAULT_CVSS_RULE: CVSSRule = {
  ruleId: null,
  cvssFilter: 'LEVEL',
  minimumCVSSLevel: 'LOW',
  minimumCVSSValue: 0,
};

const buildCweLink = (id: string) => `https://cwe.mitre.org/data/definitions/${id}.html`;
const buildCveLink = (cve: string) => `https://nvd.nist.gov/vuln/detail/${cve}`;

const DisplaySecurityPolicyPageContent: VoidFunctionComponent<ContentProps> = ({
  policy,
  isFreePlan,
}) => {
  const { data: cweList = [], isError: isCweListError, isLoading: isCweListLoading } = useFetchCweList();

  // Derive server state from policy rules
  const serverState = useMemo(() => {
    const rules = policy.rules ?? [];

    const filterRule = rules.find((r) => r.filterType === 'FILTER');
    const cvssRule: CVSSRule = filterRule
      ? {
          ruleId: filterRule.ruleId,
          cvssFilter: filterRule.cvssFilter || 'LEVEL',
          minimumCVSSLevel: filterRule.minimumCVSSLevel || 'LOW',
          minimumCVSSValue: filterRule.minimumCVSSValue || 0,
        }
      : DEFAULT_CVSS_RULE;

    const cweAllowList = rules
      .filter((r) => r.filterType === 'WHITELIST' && r.whitelistedCWE != null)
      .map((r) => String(r.whitelistedCWE));

    const cweDenyList = rules
      .filter((r) => r.filterType === 'BLACKLIST' && r.blacklistedCWE != null)
      .map((r) => String(r.blacklistedCWE));

    const cveAllowRule = rules.find((r) => r.allowedCves?.length);
    const cveAllowList: string[] = cveAllowRule?.allowedCves ?? [];

    const areDisputedCVEsIgnored = rules.some((r) => r.filterType === 'CVE_STATUS');
    const isMalwareEnabled = rules.some((r) => r.type === 'malware');

    return { cvssRule, cweAllowList, cweDenyList, cveAllowList, areDisputedCVEsIgnored, isMalwareEnabled };
  }, [policy.rules]);

  // Editable state with dirty tracking
  const { data, isDirty, onUpdate, onSave } = useEditable(serverState);
  const { cvssRule, cweAllowList, cweDenyList, cveAllowList, areDisputedCVEsIgnored, isMalwareEnabled } = data;

  const { mutate: saveRules, isPending } = useSaveSecurityPolicyRules(policy.id);

  // CWE helpers
  const isValidCWE = useCallback(
    (cwe: string): boolean => {
      const cweId = Number(cwe);
      return !Number.isNaN(cweId) &&
        Number.isInteger(cweId) &&
        cweList.some((c: CWEDescription) => c.id === cweId);
    },
    [cweList],
  );

  const getCweTitle = useCallback(
    (id: string) => cweList.find((cwe) => `${cwe.id}` === `${id}`)?.title || id,
    [cweList],
  );

  const getCweSuggestions = useCallback(
    async (term: string): Promise<Suggestion[]> => {
      return cweList
        .filter(({ id, title }) => `${id}`.includes(term) || title.toLowerCase().includes(term.toLowerCase()))
        .slice(0, 5)
        .map((cwe) => ({
          id: String(cwe.id),
          title: cwe.title,
          description: cwe.description,
        }));
    },
    [cweList],
  );

  const getCveSuggestions = useCallback(
    async (cve: string): Promise<Suggestion[]> => {
      try {
        const { data: results } = await api.get<Array<{ cve: string; description?: string }>>('/api/vulns/cve-list', true, {
          query: cve,
        });

        return results?.map((suggestion) => ({
          link: buildCveLink(suggestion.cve),
          description: suggestion.description,
          id: suggestion.cve,
        })) ?? [];
      } catch {
        return [];
      }
    },
    [],
  );

  // Save handler
  const onSavePolicy = useCallback(() => {
    const cvssRuleToAdd: Partial<SecurityRule> = {
      type: 'vulnerability',
      filterType: 'FILTER',
      minimumCVSSLevel: cvssRule.cvssFilter === 'LEVEL' ? cvssRule.minimumCVSSLevel : null,
      minimumCVSSValue: cvssRule.cvssFilter === 'VALUE' ? cvssRule.minimumCVSSValue : null,
      cvssFilter: cvssRule.cvssFilter,
    };

    const shouldSkipCweValidation = isCweListLoading || isCweListError;

    const allowedCWERules: Array<Partial<SecurityRule>> = cweAllowList
      .filter((rule) => shouldSkipCweValidation || isValidCWE(rule))
      .map((rule) => ({
        type: 'vulnerability',
        filterType: 'WHITELIST',
        whitelistedCWE: Number(rule),
      }));

    const deniedCWERules: Array<Partial<SecurityRule>> = cweDenyList
      .filter((rule) => shouldSkipCweValidation || isValidCWE(rule))
      .map((rule) => ({
        type: 'vulnerability',
        filterType: 'BLACKLIST',
        blacklistedCWE: Number(rule),
      }));

    const aggregatedRules: Array<Partial<SecurityRule>> = [
      ...deniedCWERules,
      ...allowedCWERules,
      cvssRuleToAdd,
    ];

    if (cveAllowList.length > 0) {
      aggregatedRules.push({
        type: 'vulnerability',
        allowedCves: cveAllowList,
      });
    }

    if (areDisputedCVEsIgnored) {
      aggregatedRules.push({
        type: 'vulnerability',
        filterType: 'CVE_STATUS',
      });
    }

    if (isMalwareEnabled) {
      aggregatedRules.push({
        type: 'malware',
        filterType: null,
      });
    }

    saveRules({ rules: aggregatedRules }, { onSuccess: onSave });
  }, [
    cvssRule, cweAllowList, cweDenyList, cveAllowList,
    areDisputedCVEsIgnored, isMalwareEnabled, isValidCWE, isCweListError, isCweListLoading, saveRules, onSave,
  ]);

  // Change handlers
  const onCvssRuleChange = useCallback(
    (rule: CVSSRule) => onUpdate({ cvssRule: rule }),
    [onUpdate],
  );

  const onCweAllowListChange = useCallback(
    (rules: string[]) => onUpdate({ cweAllowList: rules }),
    [onUpdate],
  );

  const onCweDenyListChange = useCallback(
    (rules: string[]) => onUpdate({ cweDenyList: rules }),
    [onUpdate],
  );

  const onCveAllowListChange = useCallback(
    (rules: string[]) => onUpdate({ cveAllowList: rules }),
    [onUpdate],
  );

  const onToggleDisputedCVEs = useCallback(
    (isChecked: boolean) => onUpdate({ areDisputedCVEsIgnored: isChecked }),
    [onUpdate],
  );

  const onToggleMalware = useCallback(
    (isChecked: boolean) => onUpdate({ isMalwareEnabled: isChecked }),
    [onUpdate],
  );

  const isMalwareFeatureEnabled = useIsFeatureEnabled(ISSUES_MALWARE);

  return (
    <>
      <EditPolicyCTA policy={policy} />
      <Helmet title={`${policy.title} - Policies`} />
      <div className="container margin-top-lg">
        <SettingsPage>
          <Header heading="Security Policy">
            <Header.Paragraph>
              Manage how security issues are assigned to projects with this policy
            </Header.Paragraph>
            <Header.SaveButton onClick={onSavePolicy} isDisabled={!isDirty} isLoading={isPending} />
          </Header>

          <PaidFeatureGate isRestricted={isFreePlan} hidePill>
            <Section>
              <Section.Left heading="Severity Filter">
                <Section.Paragraph>
                  Define which kinds of vulnerabilities should be flagged by this policy.
                  Note that issues with unknown severity information will always be included.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right>
                <CVSSFilterComponent cvssRule={cvssRule} onCvssRuleChange={onCvssRuleChange} />
              </Section.Right>
            </Section>

            <Section>
              <Section.Left heading="CWE Allow Rules">
                <Section.Paragraph>
                  Vulnerabilities with CWEs defined in these rules will <b>never</b> flag a security issue.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right isBorderless>
                {isCweListError && (
                  <div className="alert alert-danger">
                    Failed to load CWE catalog. Adding new CWE rules is disabled until the list loads successfully.
                  </div>
                )}
                <ItemizedRuleList
                  inputDecoration="CWE-"
                  rulePrefix="CWE-"
                  rules={cweAllowList}
                  onRulesChange={onCweAllowListChange}
                  getTitle={getCweTitle}
                  buildLink={buildCweLink}
                  getSuggestions={getCweSuggestions}
                  isLoading={isCweListLoading}
                  isDisabled={isCweListError}
                />
              </Section.Right>
            </Section>

            <Section>
              <Section.Left heading="CWE Deny Rules">
                <Section.Paragraph>
                  Vulnerabilities with CWEs defined in these rules will <b>always</b> flag a security issue.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right isBorderless>
                {isCweListError && (
                  <div className="alert alert-danger">
                    Failed to load CWE catalog. Adding new CWE rules is disabled until the list loads successfully.
                  </div>
                )}
                <ItemizedRuleList
                  inputDecoration="CWE-"
                  rulePrefix="CWE-"
                  rules={cweDenyList}
                  onRulesChange={onCweDenyListChange}
                  getTitle={getCweTitle}
                  buildLink={buildCweLink}
                  getSuggestions={getCweSuggestions}
                  isLoading={isCweListLoading}
                  isDisabled={isCweListError}
                />
              </Section.Right>
            </Section>

            <Section>
              <Section.Left heading="CVE Allow Rules">
                <Section.Paragraph>
                  Vulnerabilities with these CVEs will <b>never</b> flag a security issue.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right isBorderless>
                <ItemizedRuleList
                  inputDecoration="CVE-"
                  rules={cveAllowList}
                  onRulesChange={onCveAllowListChange}
                  buildLink={buildCveLink}
                  getSuggestions={getCveSuggestions}
                />
              </Section.Right>
            </Section>

            <Section>
              <Section.Left heading="CVE Filter Rules">
                <Section.Paragraph>
                  Show disputed CVEs in views and reports.
                </Section.Paragraph>
              </Section.Left>
              <Section.Right>
                <CVEFilterComponent
                  isEnabled={areDisputedCVEsIgnored}
                  onToggle={onToggleDisputedCVEs}
                />
              </Section.Right>
            </Section>

            {isMalwareFeatureEnabled && (
              <Section>
                <Section.Left heading="Malware Detection">
                  <Section.Paragraph>
                    Detect dependencies identified as malicious by cross-referencing packages
                    against known malware reports.
                  </Section.Paragraph>
                </Section.Left>
                <Section.Right>
                  <LabeledToggle
                    id="security-policy-malware-detection"
                    label="Enable Malware Detection"
                    isChecked={isMalwareEnabled}
                    onChange={onToggleMalware}
                  />
                </Section.Right>
              </Section>
            )}
          </PaidFeatureGate>
        </SettingsPage>
      </div>
    </>
  );
};

const DisplaySecurityPolicyPage: VoidFunctionComponent<DisplaySecurityPolicyPageProps> = (props) => {
  const policyId = Number(props.params.policyId);
  const hasSecurity = useIsSecurityEnabled();
  const isFreePlan = isFreeTier({
    plan: window.user?.organization?.stripe_plan,
    stripePriceLimit: window.user?.organization?.stripePriceLimit,
  });
  const isValidPage = hasSecurity && !Number.isNaN(policyId);
  const { data: policy, isLoading, isError } = useFetchPolicy(policyId, isValidPage);

  if (!isValidPage) {
    return <NotFound />;
  }
  if (isLoading) {
    return <Loader />;
  }
  if (isError) {
    return (
      <FallbackImage src="error-plug" alt="Error loading policy" hasPadding>
        Something went wrong loading this policy. Please try again later.
      </FallbackImage>
    );
  }
  if (!policy) {
    return <NotFound />;
  }

  return (
    <DisplaySecurityPolicyPageContent
      policy={policy as SecurityPolicy}
      isFreePlan={isFreePlan}
    />
  );
};

export default DisplaySecurityPolicyPage;
